import { useEffect, useMemo, useState } from 'react';

export type TableDensity = 'comfortable' | 'compact';

export type TablePreferenceColumn<TKey extends string> = {
  key: TKey;
  label: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  defaultVisible?: boolean;
  locked?: boolean;
};

export type TablePreferences<TKey extends string> = {
  widths: Record<TKey, number>;
  visibility: Record<TKey, boolean>;
  density: TableDensity;
  fontSize: number;
};

type StoredPreferences = {
  widths?: Record<string, unknown>;
  visibility?: Record<string, unknown>;
  density?: unknown;
  fontSize?: unknown;
};

const getDefaults = <TKey extends string>(columns: readonly TablePreferenceColumn<TKey>[]): TablePreferences<TKey> => {
  const widths = {} as Record<TKey, number>;
  const visibility = {} as Record<TKey, boolean>;
  columns.forEach(column => {
    widths[column.key] = column.defaultWidth;
    visibility[column.key] = column.defaultVisible !== false;
  });
  return { widths, visibility, density: 'comfortable', fontSize: 13 };
};

const restorePreferences = <TKey extends string>(
  raw: string | null,
  columns: readonly TablePreferenceColumn<TKey>[],
): TablePreferences<TKey> | null => {
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as StoredPreferences & Record<string, unknown>;
    const defaults = getDefaults(columns);
    const restored = getDefaults(columns);
    const storedWidths = stored.widths && typeof stored.widths === 'object' ? stored.widths : stored;
    columns.forEach(column => {
      const width = storedWidths[column.key];
      if (typeof width === 'number' && Number.isFinite(width)) {
        restored.widths[column.key] = Math.max(column.minWidth, Math.min(column.maxWidth, width));
      }
      const visible = stored.visibility?.[column.key];
      if (!column.locked && typeof visible === 'boolean') {
        restored.visibility[column.key] = visible;
      }
    });
    restored.density = stored.density === 'compact' ? 'compact' : defaults.density;
    if (typeof stored.fontSize === 'number' && Number.isFinite(stored.fontSize)) {
      restored.fontSize = Math.max(11, Math.min(16, stored.fontSize));
    }
    return restored;
  } catch {
    return null;
  }
};

export const useTablePreferences = <TKey extends string>(
  storageKey: string,
  username: string | null,
  columns: readonly TablePreferenceColumn<TKey>[],
  legacyStorageKey?: string,
) => {
  const defaults = useMemo(() => getDefaults(columns), [columns]);
  const scopedStorageKey = `${storageKey}:${encodeURIComponent(username || 'anonymous')}`;
  const [preferences, setPreferences] = useState(defaults);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const current = loadedKey === scopedStorageKey ? preferences : defaults;

  useEffect(() => {
    let restored: TablePreferences<TKey> | null = null;
    try {
      restored = restorePreferences(localStorage.getItem(scopedStorageKey), columns);
      if (!restored && legacyStorageKey) {
        restored = restorePreferences(localStorage.getItem(legacyStorageKey), columns);
      }
    } catch {
      restored = null;
    }
    setPreferences(restored || getDefaults(columns));
    setLoadedKey(scopedStorageKey);
  }, [columns, legacyStorageKey, scopedStorageKey]);

  useEffect(() => {
    if (loadedKey !== scopedStorageKey) return;
    try {
      localStorage.setItem(scopedStorageKey, JSON.stringify(preferences));
    } catch {
      // Keep the current in-memory preference if storage is unavailable.
    }
  }, [loadedKey, preferences, scopedStorageKey]);

  const setWidth = (key: TKey, value: number) => {
    const column = columns.find(item => item.key === key);
    if (!column || !Number.isFinite(value)) return;
    const width = Math.max(column.minWidth, Math.min(column.maxWidth, value));
    setPreferences(previous => ({ ...previous, widths: { ...previous.widths, [key]: width } }));
  };

  const setVisible = (key: TKey, visible: boolean) => {
    const column = columns.find(item => item.key === key);
    if (!column || column.locked) return;
    setPreferences(previous => ({
      ...previous,
      visibility: { ...previous.visibility, [key]: visible },
    }));
  };

  const setDensity = (density: TableDensity) => {
    setPreferences(previous => ({ ...previous, density }));
  };

  const setFontSize = (fontSize: number) => {
    if (!Number.isFinite(fontSize)) return;
    setPreferences(previous => ({ ...previous, fontSize: Math.max(11, Math.min(16, fontSize)) }));
  };

  const reset = () => setPreferences(getDefaults(columns));

  return { preferences: current, setWidth, setVisible, setDensity, setFontSize, reset };
};
